import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import PrimeProLeads from "@/components/PrimeProLeads";

const TABS = ["Prime Pro Leads", "Requests", "Professionals", "Settings"];

const fmtTime = (s) => (s == null ? "—" : s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s / 60)}m` : `${Math.round(s / 3600)}h`);

const RequestDetail = ({ id }) => {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/admin/requests/${id}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [id]);
  if (!detail) return <p className="text-xs text-slate-400 p-4">Loading detail…</p>;
  return (
    <div className="bg-[#F4F5F7] border-t border-border p-5 space-y-5 text-sm" data-testid={`admin-request-detail-${id}`}>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-navy mb-2">Leads sent ({detail.leads.length})</p>
        {detail.leads.length === 0 ? <p className="text-xs text-slate-400">No matching professionals at time of submission.</p> : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-400">
              <th className="py-1 pr-3">Company</th><th className="py-1 pr-3">Status</th>
              <th className="py-1 pr-3">Responded</th><th className="py-1">Response time</th>
            </tr></thead>
            <tbody>
              {detail.leads.map((l) => (
                <tr key={l.id} className="border-t border-border">
                  <td className="py-1.5 pr-3 font-medium text-navy">{l.company_name}</td>
                  <td className="py-1.5 pr-3 capitalize">{l.status.replace(/_/g, " ")}</td>
                  <td className="py-1.5 pr-3">{l.responded_at ? new Date(l.responded_at).toLocaleString() : "—"}</td>
                  <td className="py-1.5">{fmtTime(l.response_time_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-navy mb-2">Offers ({detail.offers.length})</p>
        {detail.offers.length === 0 ? <p className="text-xs text-slate-400">No offers yet.</p> :
          detail.offers.map((o) => (
            <p key={o.id} className="text-xs text-slate-600 py-1 border-t border-border">
              <strong className="text-navy">{o.company_name}</strong>
              {o.amount != null && ` · $${o.amount.toLocaleString()}`} · {o.message} · <span className="capitalize">{o.status}</span>
            </p>
          ))}
      </div>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-navy mb-2">Appointments ({detail.appointments.length})</p>
        {detail.appointments.length === 0 ? <p className="text-xs text-slate-400">No appointment actions.</p> :
          detail.appointments.map((a) => (
            <p key={a.id} className="text-xs text-slate-600 py-1 border-t border-border">
              <strong className="text-navy">{a.company_name}</strong> · {a.action === "accept" ? "accepted" : "suggested"} {a.date} {a.time_window || ""}
            </p>
          ))}
      </div>
      {detail.final_professional && (
        <p className="text-xs text-green-700 font-bold">Final selected professional: {detail.final_professional.company_name}</p>
      )}
    </div>
  );
};

const RequestsTab = () => {
  const [requests, setRequests] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/requests").then((r) => setRequests(r.data)).catch(() => setRequests([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/requests/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (requests === null) return <p className="text-slate-400 text-sm">Loading…</p>;
  return (
    <div className="space-y-3" data-testid="admin-requests-list">
      {requests.length === 0 && <p className="text-slate-400 text-sm">No service requests yet.</p>}
      {requests.map((r) => (
        <div key={r.id} className="bg-white border border-border">
          <div className="p-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-black text-navy tracking-tight">{r.category_name}</h3>
                  {r.is_emergency ? (
                    <span className="flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">
                      <AlertTriangle size={10} /> Emergency
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 border border-border px-2 py-0.5">Non-emergency</span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {r.city}, {r.zip_code} · {r.contact_name} · {new Date(r.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select data-testid={`admin-status-select-${r.id}`} value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)}
                  className="h-9 border border-border px-2 text-xs bg-white capitalize">
                  {["new", "matching", "offers_received", "appointment_pending", "booked", "in_progress", "completed", "cancelled", "expired"].map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                  ))}
                </select>
                <button data-testid={`admin-expand-${r.id}`} onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  className="text-slate-400 hover:text-navy">
                  {expanded === r.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-5 mt-3 text-xs text-slate-500 flex-wrap">
              <span><strong className="text-navy">{r.leads_sent}</strong> pros received</span>
              <span><strong className="text-navy">{r.responses}</strong> responded</span>
              <span><strong className="text-navy">{r.offers_count}</strong> offers</span>
              <span>Appointment: <strong className="text-navy">{r.appointment_status || "n/a"}</strong></span>
              {r.final_professional && <span className="text-green-700 font-bold">Selected: {r.final_professional}</span>}
            </div>
          </div>
          {expanded === r.id && <RequestDetail id={r.id} />}
        </div>
      ))}
    </div>
  );
};

const ProfessionalsTab = () => {
  const [pros, setPros] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/professionals").then((r) => setPros(r.data)).catch(() => setPros([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = async (id, payload) => {
    try {
      await api.put(`/admin/professionals/${id}`, payload);
      toast.success("Professional updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (pros === null) return <p className="text-slate-400 text-sm">Loading…</p>;
  return (
    <div className="space-y-3" data-testid="admin-pros-list">
      {pros.length === 0 && <p className="text-slate-400 text-sm">No professional applications yet.</p>}
      {pros.map((p) => (
        <div key={p.id} className="bg-white border border-border p-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-heading font-black text-navy tracking-tight">{p.company_name}</h3>
              <p className="text-xs text-slate-400 mt-1">{p.email} · {p.phone} · {(p.category_slugs || []).join(", ")} · {(p.city_slugs || []).join(", ")}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] font-bold uppercase tracking-widest border px-3 py-1 ${
                p.application_status === "approved" ? "bg-green-50 text-green-700 border-green-400"
                : p.application_status === "rejected" ? "bg-red-50 text-red-600 border-red-300"
                : "bg-amber-50 text-amber-600 border-amber-300"}`}>
                {p.application_status}
              </span>
              {p.application_status !== "approved" && (
                <button data-testid={`admin-approve-${p.id}`} onClick={() => update(p.id, { application_status: "approved" })}
                  className="bg-green-600 hover:bg-green-700 text-white text-[11px] font-heading font-bold uppercase tracking-wider px-4 py-2 transition-colors">
                  Approve
                </button>
              )}
              {p.application_status !== "rejected" && (
                <button data-testid={`admin-reject-${p.id}`} onClick={() => update(p.id, { application_status: "rejected" })}
                  className="border border-red-300 text-red-600 hover:bg-red-50 text-[11px] font-heading font-bold uppercase tracking-wider px-4 py-2 transition-colors">
                  Reject
                </button>
              )}
              <button data-testid={`admin-toggle-active-${p.id}`} onClick={() => update(p.id, { active: p.active === false })}
                className="border border-border text-navy hover:border-navy text-[11px] font-heading font-bold uppercase tracking-wider px-4 py-2 transition-colors">
                {p.active === false ? "Activate" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const SettingsTab = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    try {
      await api.put("/admin/settings", s);
      toast.success("Lead matching settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  if (!s) return <p className="text-slate-400 text-sm">Loading…</p>;
  return (
    <div className="bg-white border border-border p-8 max-w-lg" data-testid="admin-settings">
      <h3 className="font-heading font-black text-navy text-xl tracking-tight mb-6">Lead Matching Settings</h3>
      <div className="space-y-5">
        <div>
          <label className="text-sm font-medium text-navy block mb-2">Max professionals who receive each lead</label>
          <input data-testid="admin-setting-recipients" type="number" min="1" max="50" value={s.max_professionals_per_lead}
            onChange={(e) => setS({ ...s, max_professionals_per_lead: parseInt(e.target.value || "1", 10) })}
            className="w-full h-12 border border-border px-4 text-sm focus:outline-none focus:ring-2 focus:ring-flame" />
        </div>
        <div>
          <label className="text-sm font-medium text-navy block mb-2">Max offers accepted per request (response cap)</label>
          <input data-testid="admin-setting-offers" type="number" min="1" max="50" value={s.max_offers_per_request}
            onChange={(e) => setS({ ...s, max_offers_per_request: parseInt(e.target.value || "1", 10) })}
            className="w-full h-12 border border-border px-4 text-sm focus:outline-none focus:ring-2 focus:ring-flame" />
        </div>
        <button data-testid="admin-settings-save" onClick={save}
          className="bg-flame hover:bg-flame-dark text-white font-heading font-bold uppercase tracking-wider text-sm px-8 py-3.5 transition-colors">
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Prime Pro Leads");
  const [newCount, setNewCount] = useState(0);

  useEffect(() => {
    api.get("/admin/leads/stats").then((r) => setNewCount(r.data.new)).catch(() => {});
  }, []);

  if (user && !["admin", "super_admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <p className="uppercase tracking-[0.25em] text-flame font-heading font-bold text-sm mb-2">Yosi Media Studio · Master Admin</p>
        <h1 data-testid="admin-heading" className="font-heading font-black tracking-tighter text-navy text-4xl">Prime Pro Control</h1>
        <div className="flex gap-1 mt-8 mb-8 border-b border-border overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} data-testid={`admin-tab-${t.toLowerCase().replace(/ /g, "-")}`} onClick={() => setTab(t)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-heading font-bold uppercase tracking-wider border-b-2 whitespace-nowrap transition-colors ${
                tab === t ? "border-flame text-navy" : "border-transparent text-slate-400 hover:text-navy"}`}>
              {t}
              {t === "Prime Pro Leads" && newCount > 0 && (
                <span data-testid="primepro-leads-badge" className="bg-flame text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {newCount} New
                </span>
              )}
            </button>
          ))}
        </div>
        {tab === "Prime Pro Leads" && <PrimeProLeads onStatsChange={(s) => setNewCount(s.new)} />}
        {tab === "Requests" && <RequestsTab />}
        {tab === "Professionals" && <ProfessionalsTab />}
        {tab === "Settings" && <SettingsTab />}
      </div>
    </div>
  );
}
